import { Clock, ArrowRight, CheckCircle } from "lucide-react"
import { useNavigate } from "react-router-dom"
import type { Course } from "../types/api"
import iconSrc from "../../../../resources/icon.png"
import { appInfo } from "@renderer/config/appinfo"

interface Props {
  course: Course
  highlights: string[]
  computedDuration?: string
  labProgress?: number
}

export const CourseCard = ({ course, highlights, computedDuration, labProgress }: Props) => {
  const navigate = useNavigate()

  const level =
    course.level?.toLowerCase() === "advanced"
      ? "Advanced"
      : course.level?.toLowerCase() === "intermediate"
      ? "Intermediate"
      : "Beginner"

  const isStarted = (labProgress ?? 0) > 0

  return (
    <div className="group bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all shadow-[0_4px_24px_rgba(0,0,0,0.07)] hover:shadow-xl hover:-translate-y-1 w-full">

      {/* IMAGE */}
      <div className="relative h-[148px] overflow-hidden">
        <img
          src={course.thumbnail ? `${appInfo.apiDomain}/storage/${course.thumbnail}` : iconSrc}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          onError={(e) => { e.currentTarget.src = iconSrc }}
        />

        {/* LEVEL BADGE */}
        <span className="absolute top-3 right-3 text-xs font-semibold px-3 py-1 rounded-full text-white" style={{ background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.28)' }}>
          {level}
        </span>

        {/* PROGRESS BAR ON IMAGE */}
        {isStarted && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
            <div className="h-full bg-[#7a6020] transition-all" style={{ width: `${labProgress}%` }} />
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div className="p-4 flex flex-col gap-2.5">

        {/* Duration + progress label */}
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span>{computedDuration || course.duration || '—'}</span>
          </div>
          {isStarted && (
            <span className="font-semibold text-[#7a6020]">{labProgress}% done</span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 leading-snug">
          {course.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-gray-600 line-clamp-2">
          {course.description || "Learn core concepts with real-world exercises and examples."}
        </p>

        {/* Progress bar (below content) */}
        {isStarted && (
          <div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full bg-[#7a6020] transition-all" style={{ width: `${labProgress}%` }} />
            </div>
          </div>
        )}

        {/* Highlights (only when not started) */}
        {!isStarted && (
          <div className="flex flex-col gap-1 mt-1">
            {highlights.slice(0, 2).map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle size={14} className="text-[#7a6020]" />
                {item}
              </div>
            ))}
          </div>
        )}

        {/* BUTTON */}
        <button
          onClick={() => navigate(`/course/${course.id}/modules`)}
          className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium hover:opacity-90 transition text-sm"
          style={isStarted
            ? { background: '#f5ede0', color: '#7a6020', border: '1.5px solid #e0d4be' }
            : { background: '#7a6020', color: '#ffffff' }
          }
        >
          {isStarted ? 'Continue Learning' : 'Start Learning'}
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  )
}